
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Download, Printer, X } from 'lucide-react';
import { AdminDocument } from '@/types/admin.types';
import { toast } from 'sonner';

interface AdminDocumentViewerDialogProps {
  document: AdminDocument | null;
  isOpen: boolean;
  onClose: () => void;
}

const AdminDocumentViewerDialog = ({ document, isOpen, onClose }: AdminDocumentViewerDialogProps) => {
  if (!document) return null;

  const handleDownload = () => {
    try {
      const link = window.document.createElement('a');
      link.href = document.url;
      link.download = document.name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePrint = () => {
    try {
      if (document.type.startsWith('image/')) {
        // For images, open in new window and print
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Print Document - ${document.name}</title>
                <style>
                  body { margin: 0; padding: 20px; text-align: center; }
                  img { max-width: 100%; max-height: 90vh; }
                  @media print { 
                    body { padding: 0; }
                    .no-print { display: none; }
                  }
                </style>
              </head>
              <body>
                <div class="no-print" style="margin-bottom: 20px;">
                  <button onclick="window.print()" style="padding: 10px 20px; margin-right: 10px;">Print</button>
                  <button onclick="window.close()" style="padding: 10px 20px;">Close</button>
                </div>
                <h2>${document.name}</h2>
                <img src="${document.url}" alt="${document.name}" />
              </body>
            </html>
          `);
          printWindow.document.close();
          setTimeout(() => printWindow.focus(), 100);
        }
      } else {
        // For other file types, open in new tab
        window.open(document.url, '_blank');
      }
      toast.success('Opening document for printing');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open document for printing');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImage = document.type.startsWith('image/');
  const isPDF = document.type === 'application/pdf';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {document.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Document Info */}
          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium">Type:</span>
                <p className="text-muted-foreground">{document.documentType || 'general'}</p>
              </div>
              <div>
                <span className="font-medium">Size:</span>
                <p className="text-muted-foreground">{formatFileSize(document.size)}</p>
              </div>
              <div>
                <span className="font-medium">Added:</span>
                <p className="text-muted-foreground">
                  {document.dateAdded ? new Date(document.dateAdded).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <span className="font-medium">Status:</span>
                <p className="text-muted-foreground">{document.status || 'valid'}</p>
              </div>
            </div>
          </div>

          {/* Document Preview */}
          <div className="border rounded-lg p-4 min-h-[400px] flex items-center justify-center">
            {isImage ? (
              <img 
                src={document.url} 
                alt={document.name}
                className="max-w-full max-h-[400px] object-contain"
              />
            ) : isPDF ? (
              <div className="text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium mb-2">PDF Document</p>
                <p className="text-muted-foreground mb-4">Click "View Full Document" to open in new tab</p>
                <Button onClick={() => window.open(document.url, '_blank')}>
                  View Full Document
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium mb-2">Document File</p>
                <p className="text-muted-foreground mb-4">Click download to view this document</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
          <Button variant="outline" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDocumentViewerDialog;
